import {
  Badge,
  Button,
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  GroupBadge,
  Hint,
  Input,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  type TableSortDirection,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  Dumbbell,
  Eye,
  EyeOff,
  Image as ImageIcon,
  MoreVertical,
  Pencil,
  Plus,
  Search,
  Trash2,
  Upload,
  X,
} from 'lucide-react'
import { useState } from 'react'
import { z } from 'zod'

import { ApiError, errorMessage } from '@/api/errors'
import { useExercises, usePatchExercise } from '@/api/queries'
import type { AdminExercise, ExerciseId, ExerciseSort, SortOrder } from '@/api/types'
import { EXERCISE_SORTS, EXERCISE_SOURCES, MUSCLE_GROUP_KEYS, SORT_ORDERS } from '@/api/types'
import { CursorPager } from '@/components/data/cursor-pager'
import { PageHeader } from '@/components/layout/app-shell'
import { DeleteExerciseDialog } from '@/features/exercises/delete-exercise-dialog'
import { ExerciseDialog } from '@/features/exercises/exercise-dialog'
import { formatNumber, formatRelative, plural, SOURCE_LABELS } from '@/lib/format'
import { useDebounced } from '@/lib/hooks'
import { sortableColumn } from '@/lib/sortable-column'
import { notify } from '@/lib/toast'
import { useCursorPagination } from '@/lib/use-cursor-pagination'
import { cn } from '@/lib/utils'

/**
 * I filtri vivono nella query string, non in `useState`.
 *
 * Così un elenco filtrato è un link: "guarda queste 12 bozze senza immagini" si
 * manda in chat, e il tasto indietro del browser fa quello che l'operatore si
 * aspetta. `zod` valida i parametri, quindi un valore scritto a mano nell'URL non
 * arriva mai come filtro non previsto all'API.
 */
const searchSchema = z.object({
  q: z.string().optional(),
  source: z.enum(EXERCISE_SOURCES).optional(),
  published: z.boolean().optional(),
  group: z.enum(MUSCLE_GROUP_KEYS).optional(),
  sort: z.enum(EXERCISE_SORTS).optional(),
  order: z.enum(SORT_ORDERS).optional(),
})

type ExerciseSearch = z.infer<typeof searchSchema>

export const Route = createFileRoute('/_app/esercizi/')({
  validateSearch: searchSchema,
  component: ExercisesPage,
})

const PAGE_SIZE = 25

/** Default del server (`resolveSort(EXERCISE_SORTS, 'name', …)`). */
const DEFAULT_SORT: ExerciseSort = 'name'

/** Verso naturale di ogni campo, uguale a `defaultDirection` lato server. */
const SORT_DIRECTIONS: Record<ExerciseSort, SortOrder> = {
  name: 'asc',
  updatedAt: 'desc',
}

const SORT_LABELS: Record<ExerciseSort, string> = {
  name: 'Nome',
  updatedAt: 'Ultima modifica',
}

function ExercisesPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })

  const [queryDraft, setQueryDraft] = useState(search.q ?? '')
  const debouncedQuery = useDebounced(queryDraft, 350)

  const [editing, setEditing] = useState<{ id: ExerciseId | undefined } | null>(null)
  const [deleting, setDeleting] = useState<AdminExercise | null>(null)

  const sort = search.sort ?? DEFAULT_SORT
  const order = search.order ?? SORT_DIRECTIONS[sort]

  const params = {
    ...(debouncedQuery ? { query: debouncedQuery } : {}),
    ...(search.source ? { source: search.source } : {}),
    ...(search.published !== undefined ? { published: search.published } : {}),
    ...(search.group ? { group: search.group } : {}),
    sort,
    order,
    limit: PAGE_SIZE,
  }

  // `sort` e `order` sono dentro `params`, quindi dentro la `resetKey`: cambiare
  // ordinamento azzera il cursore nello stesso render, prima che parta la
  // richiesta. Il cursore appartiene a un ordinamento e riusarlo è `400`.
  const pagination = useCursorPagination(JSON.stringify(params))
  const list = useExercises({
    ...params,
    ...(pagination.cursor ? { cursor: pagination.cursor } : {}),
  })

  const setFilter = <K extends keyof ExerciseSearch>(key: K, value: ExerciseSearch[K]) => {
    void navigate({
      search: (previous) => {
        const next = { ...previous }
        if (value === undefined) delete next[key]
        else next[key] = value
        return next
      },
    })
  }

  const setSort = (field: ExerciseSort, direction: TableSortDirection) => {
    void navigate({ search: (previous) => ({ ...previous, sort: field, order: direction }) })
  }

  /** Le props di ordinamento, colonna per colonna. */
  const column = sortableColumn(sort, order, setSort)

  const hasFilters =
    Boolean(queryDraft) ||
    search.source !== undefined ||
    search.published !== undefined ||
    search.group !== undefined

  const clearFilters = () => {
    setQueryDraft('')
    // L'ordinamento non è un filtro e sopravvive all'azzeramento.
    void navigate({ search: { sort: search.sort, order: search.order } })
  }

  const rows = list.data?.data ?? []

  return (
    <>
      <PageHeader
        title="Catalogo esercizi"
        description={
          <>
            Include gli esercizi non pubblicati, che l'app non vede. Le modifiche sono tracciate
            nell'audit log.
          </>
        }
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/esercizi/bulk">
                <Upload />
                Carica in blocco
              </Link>
            </Button>
            <Button variant="primary" onClick={() => setEditing({ id: undefined })}>
              <Plus />
              Nuovo
            </Button>
          </>
        }
      />

      {/* Una sola riga di filtri sopra tutto ciò che governa. */}
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative min-w-0 flex-1 sm:max-w-xs">
          <Search
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
            aria-hidden
          />
          <Input
            value={queryDraft}
            onChange={(event) => {
              setQueryDraft(event.target.value)
              setFilter('q', event.target.value || undefined)
            }}
            placeholder="Cerca nome, attrezzatura, muscoli…"
            aria-label="Cerca nel catalogo"
            className="pl-9"
            type="search"
          />
        </div>

        <div className="grid grid-cols-2 gap-2 sm:flex sm:w-auto">
          <Select
            aria-label="Filtra per stato di pubblicazione"
            value={search.published === undefined ? 'all' : search.published ? 'yes' : 'no'}
            onValueChange={(value) =>
              setFilter('published', value === 'all' ? undefined : value === 'yes')
            }
            className="sm:w-40"
            items={[
              { value: 'all', label: 'Tutti gli stati' },
              { value: 'yes', label: 'Pubblicati' },
              { value: 'no', label: 'Da rivedere' },
            ]}
          />

          <Select
            aria-label="Filtra per sorgente"
            value={search.source ?? 'all'}
            onValueChange={(value) =>
              setFilter('source', value === 'all' ? undefined : (value as ExerciseSearch['source']))
            }
            className="sm:w-36"
            items={[
              { value: 'all', label: 'Ogni sorgente' },
              ...EXERCISE_SOURCES.map((source) => ({
                value: source,
                label: SOURCE_LABELS[source],
              })),
            ]}
          />

          <Select
            aria-label="Filtra per gruppo muscolare"
            value={search.group ?? 'all'}
            onValueChange={(value) =>
              setFilter('group', value === 'all' ? undefined : (value as ExerciseSearch['group']))
            }
            className="col-span-2 sm:w-40"
            items={[
              { value: 'all', label: 'Ogni gruppo' },
              // `GroupBadge` invece del pallino fatto a mano: il colore del gruppo è
              // contract (reference/muscle-groups.json) e nel design system arriva
              // dai token, non da una tabella copiata qui. Il testo resta dentro il
              // badge, quindi la ricerca a tastiera di Radix continua a funzionare.
              ...MUSCLE_GROUP_KEYS.map((group) => ({
                value: group,
                label: <GroupBadge group={group} size="sm" />,
              })),
            ]}
          />

          {/* Sotto `lg` l'elenco è di card e non c'è un'intestazione su cui
              cliccare: stessi parametri, controllo diverso. */}
          <Select
            aria-label="Ordina il catalogo"
            value={`${sort}:${order}`}
            onValueChange={(value) => {
              const [field, direction] = value.split(':')
              setSort(field as ExerciseSort, direction as TableSortDirection)
            }}
            className="col-span-2 lg:hidden"
            items={EXERCISE_SORTS.flatMap((field) =>
              SORT_ORDERS.map((direction) => ({
                value: `${field}:${direction}`,
                label: `${SORT_LABELS[field]} — ${direction === 'asc' ? 'crescente' : 'decrescente'}`,
              })),
            )}
          />
        </div>

        {hasFilters ? (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="sm:ml-auto">
            <X />
            Azzera filtri
          </Button>
        ) : null}
      </div>

      {list.isError && !list.data ? (
        <ErrorState
          message={errorMessage(list.error)}
          requestId={list.error instanceof ApiError ? list.error.requestId : undefined}
          onRetry={() => void list.refetch()}
        />
      ) : list.isPending ? (
        <ListSkeleton />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Dumbbell />}
          title={hasFilters ? 'Nessun esercizio con questi filtri' : 'Catalogo vuoto'}
          description={
            hasFilters
              ? 'Prova ad allargare la ricerca o azzera i filtri.'
              : 'Importa il dataset seed o crea il primo esercizio a mano.'
          }
          action={
            hasFilters ? (
              <Button variant="outline" onClick={clearFilters}>
                Azzera filtri
              </Button>
            ) : (
              <Button variant="primary" asChild>
                <Link to="/import">Avvia un import</Link>
              </Button>
            )
          }
        />
      ) : (
        <>
          {/* Sotto lg: elenco di card. La tabella non si restringe, cambia forma. */}
          <div
            className={cn(
              'transition-opacity',
              // Nessuno skeleton sul refetch: si tiene il render precedente
              // attenuato, così il layout non salta a ogni carattere digitato.
              list.isFetching && 'opacity-60',
            )}
          >
            <ul className="flex flex-col gap-2 lg:hidden">
              {rows.map((exercise) => (
                <ExerciseCard
                  key={exercise.id}
                  exercise={exercise}
                  onEdit={() => setEditing({ id: exercise.id })}
                  onDelete={() => setDeleting(exercise)}
                />
              ))}
            </ul>

            <div className="border-border bg-card hidden rounded-lg border lg:block">
              <Table>
                <TableHead>
                  <TableRow className="hover:bg-transparent">
                    {/* Gruppo, sorgente, stato e uso in schede non sono
                        ordinabili: `AdminExerciseListQuery` accetta solo `name` e
                        `updatedAt`, e un'intestazione cliccabile che risponde
                        `400` è peggio di una che non lo sembra. */}
                    <TableHeaderCell {...column('name', SORT_DIRECTIONS.name)}>
                      Esercizio
                    </TableHeaderCell>
                    <TableHeaderCell>Gruppo</TableHeaderCell>
                    <TableHeaderCell>Sorgente</TableHeaderCell>
                    <TableHeaderCell>Stato</TableHeaderCell>
                    <TableHeaderCell className="text-right">In schede</TableHeaderCell>
                    {/* Colonna nuova, e non decorativa: «cosa è cambiato» è la
                        prima domanda dopo un import, ed è l'unica per cui
                        `updatedAt` è ordinabile lato server. */}
                    <TableHeaderCell {...column('updatedAt', SORT_DIRECTIONS.updatedAt)}>
                      Modificato
                    </TableHeaderCell>
                    <TableHeaderCell className="w-12">
                      <span className="sr-only">Azioni</span>
                    </TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((exercise) => (
                    <ExerciseRow
                      key={exercise.id}
                      exercise={exercise}
                      onEdit={() => setEditing({ id: exercise.id })}
                      onDelete={() => setDeleting(exercise)}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <CursorPager
            meta={list.data?.meta}
            page={pagination.page}
            pageCount={rows.length}
            canGoBack={pagination.canGoBack}
            onNext={() => pagination.next(list.data?.meta.nextCursor)}
            onPrevious={pagination.previous}
            isFetching={list.isFetching}
            itemLabel="esercizi"
          />
        </>
      )}

      <ExerciseDialog
        exerciseId={editing?.id}
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
      />
      <DeleteExerciseDialog
        exercise={deleting}
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
      />
    </>
  )
}

// ── stato di pubblicazione ────────────────────────────────────────────────────

/**
 * Interruttore pubblica/nascondi.
 *
 * Usa `PATCH` con il solo campo `published`: sostituire l'intera risorsa per
 * cambiare un booleano rischierebbe di azzerare i campi che l'elenco non porta.
 * Non manda `If-Match` perché la lettura dell'elenco non espone l'`ETag` per riga:
 * per un singolo booleano l'ultima scrittura che vince è un esito accettabile.
 */
function PublishToggle({ exercise }: { exercise: AdminExercise }) {
  const patch = usePatchExercise()
  const next = !exercise.published

  const onToggle = async () => {
    try {
      await patch.mutateAsync({ id: exercise.id, patch: { published: next } })
      notify({
        message: next ? 'Esercizio pubblicato.' : 'Esercizio nascosto dall’app.',
        variant: 'success',
      })
    } catch (error) {
      notify({ message: errorMessage(error), variant: 'danger' })
    }
  }

  return (
    <Hint label={next ? 'Pubblica: diventa visibile nell’app' : 'Nascondi dall’app'}>
      <Button
        size="icon-sm"
        variant="ghost"
        loading={patch.isPending}
        onClick={() => void onToggle()}
        aria-label={next ? 'Pubblica esercizio' : 'Nascondi esercizio'}
      >
        {next ? <Eye /> : <EyeOff />}
      </Button>
    </Hint>
  )
}

function StatusBadge({ exercise }: { exercise: AdminExercise }) {
  if (exercise.deletedAt) {
    return <Badge variant="danger">Eliminato</Badge>
  }
  return exercise.published ? (
    <Badge variant="success">Pubblicato</Badge>
  ) : (
    <Badge variant="warning">Da rivedere</Badge>
  )
}

// ── riga tabella (desktop) ────────────────────────────────────────────────────

function ExerciseRow({
  exercise,
  onEdit,
  onDelete,
}: {
  exercise: AdminExercise
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-3">
          <Thumbnail exercise={exercise} />
          <div className="min-w-0">
            <button
              type="button"
              onClick={onEdit}
              className="block max-w-md truncate text-left text-sm font-medium hover:underline"
            >
              {exercise.name}
            </button>
            <p className="text-muted-foreground truncate text-xs">
              {exercise.primaryMuscles.join(', ') || 'nessun muscolo primario'}
            </p>
          </div>
        </div>
      </TableCell>
      <TableCell>{exercise.group ? <GroupBadge group={exercise.group} /> : '—'}</TableCell>
      <TableCell>
        <Badge variant="outline">{SOURCE_LABELS[exercise.source]}</Badge>
      </TableCell>
      <TableCell>
        <StatusBadge exercise={exercise} />
      </TableCell>
      <TableCell className="text-right" data-numeric>
        {typeof exercise.cardUsageCount === 'number' ? (
          <span className={cn(exercise.cardUsageCount > 0 && 'font-medium')}>
            {formatNumber(exercise.cardUsageCount)}
          </span>
        ) : (
          '—'
        )}
      </TableCell>
      <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
        {formatRelative(exercise.updatedAt)}
      </TableCell>
      <TableCell>
        <div className="flex items-center justify-end gap-0.5">
          <PublishToggle exercise={exercise} />
          <RowMenu onEdit={onEdit} onDelete={onDelete} />
        </div>
      </TableCell>
    </TableRow>
  )
}

// ── card (mobile) ─────────────────────────────────────────────────────────────

function ExerciseCard({
  exercise,
  onEdit,
  onDelete,
}: {
  exercise: AdminExercise
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <li>
      <Card radius="md" padding="sm">
        <div className="flex items-start gap-3">
          <Thumbnail exercise={exercise} />

          <div className="min-w-0 flex-1">
            <button type="button" onClick={onEdit} className="block w-full text-left">
              <span className="line-clamp-2 text-sm font-medium">{exercise.name}</span>
            </button>
            <p className="text-muted-foreground mt-0.5 truncate text-xs">
              {exercise.primaryMuscles.join(', ') || 'nessun muscolo primario'}
            </p>

            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <StatusBadge exercise={exercise} />
              {exercise.group ? <GroupBadge group={exercise.group} /> : null}
              <Badge variant="outline">{SOURCE_LABELS[exercise.source]}</Badge>
              {typeof exercise.cardUsageCount === 'number' && exercise.cardUsageCount > 0 ? (
                <Badge variant="neutral">
                  {plural(exercise.cardUsageCount, 'scheda', 'schede')}
                </Badge>
              ) : null}
            </div>
          </div>

          <div className="flex shrink-0 flex-col items-center gap-0.5">
            <PublishToggle exercise={exercise} />
            <RowMenu onEdit={onEdit} onDelete={onDelete} />
          </div>
        </div>
      </Card>
    </li>
  )
}

function RowMenu({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="icon-sm" variant="ghost" aria-label="Altre azioni">
          <MoreVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onSelect={onEdit}>
          <Pencil />
          Modifica
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem destructive onSelect={onDelete}>
          <Trash2 />
          Elimina
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * Anteprima dalla prima immagine.
 *
 * Due casi distinti, e vanno distinti anche a schermo perché portano a decisioni
 * diverse:
 *
 * - `images` vuoto: l'esercizio non ha nulla da mostrare nell'app, va lasciato non
 *   pubblicato;
 * - `images` valorizzato ma l'URL non risponde: l'invariante del contract ("URL
 *   assoluti raggiungibili") è violata, e il dato va corretto. Un'icona di immagine
 *   rotta del browser non lo direbbe, quindi si intercetta `onError`.
 */
function Thumbnail({ exercise }: { exercise: AdminExercise }) {
  const src = exercise.images[0]
  const [failed, setFailed] = useState(false)

  if (!src || failed) {
    const label = src ? 'Immagine non raggiungibile' : 'Nessuna immagine'
    return (
      <span
        className={cn(
          'flex size-10 shrink-0 items-center justify-center rounded-sm border border-dashed',
          src ? 'border-danger/50 text-danger-content' : 'border-border text-muted-foreground',
        )}
        title={src ? `${label}: ${src}` : label}
      >
        <ImageIcon className="size-4" aria-hidden />
        <span className="sr-only">{label}</span>
      </span>
    )
  }

  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      className="border-border bg-muted size-10 shrink-0 rounded-sm border object-cover"
    />
  )
}

function ListSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 8 }, (_, index) => (
        <div
          key={index}
          className="border-border bg-card flex items-center gap-3 rounded-md border p-3"
        >
          <Skeleton className="size-10 shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-3 w-1/3" />
          </div>
          <Skeleton className="h-6 w-20 shrink-0" />
        </div>
      ))}
    </div>
  )
}
